import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { auditService } from '../../../services/auditService';
import { useAuth } from '../../../contexts/AuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';

const AuditExecutionModal = ({ isOpen, onClose, audit }) => {
  const { userProfile } = useAuth();
  const { t } = useLanguage();
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  const predefinedQuestions = {
    safety: [
      { text: 'Are all workers wearing appropriate PPE?', category: 'Personal Protective Equipment' },
      { text: 'Are emergency exits clearly marked and accessible?', category: 'Emergency Preparedness' },
      { text: 'Are fire extinguishers present and serviced?', category: 'Fire Safety' },
      { text: 'Are scaffolds properly erected and inspected?', category: 'Working at Height' },
      { text: 'Are hazardous materials properly stored and labeled?', category: 'Hazardous Materials' }
    ],
    quality: [
      { text: 'Are materials stored in appropriate conditions?', category: 'Material Management' },
      { text: 'Is workmanship meeting specification standards?', category: 'Workmanship' },
      { text: 'Are quality control checks being performed?', category: 'Quality Control' },
      { text: 'Are defects being recorded and addressed?', category: 'Defect Management' }
    ],
    environmental: [
      { text: 'Is waste being segregated correctly?', category: 'Waste Management' },
      { text: 'Are dust suppression measures in place?', category: 'Air Quality' },
      { text: 'Is water runoff being controlled?', category: 'Water Management' },
      { text: 'Are noise levels within acceptable limits?', category: 'Noise Control' }
    ],
    regulatory: [
      { text: 'Are all required permits displayed?', category: 'Permits & Licenses' },
      { text: 'Is site signage compliant with regulations?', category: 'Signage' },
      { text: 'Are inspection records up to date?', category: 'Record Keeping' },
      { text: 'Are subcontractor insurances current?', category: 'Insurance' }
    ]
  };

  useEffect(() => {
    if (audit) {
      loadAuditData();
    }
  }, [audit]);

  const loadAuditData = async () => {
    try {
      setLoading(true);
      
      let questionsResult = await auditService?.getAuditQuestions(audit?.id);
      
      // If no questions exist and audit is not completed, create predefined questions.
      // Previously gated on status === 'draft', which silently skipped any audit that
      // had already advanced to 'in_progress' before its first modal open. See wqugEUH0.
      if (questionsResult?.data?.length === 0 && audit?.status !== 'completed') {
        const auditType = audit?.audit_name?.toLowerCase()?.includes('safety') ? 'safety' :
                         audit?.audit_name?.toLowerCase()?.includes('quality') ? 'quality' :
                         audit?.audit_name?.toLowerCase()?.includes('environmental') ? 'environmental' :
                         'regulatory';
        
        const templateQuestions = predefinedQuestions?.[auditType] || predefinedQuestions?.safety;
        
        for (let i = 0; i < templateQuestions?.length; i++) {
          await auditService?.addAuditQuestion({
            auditId: audit?.id,
            questionText: templateQuestions?.[i]?.text,
            category: templateQuestions?.[i]?.category,
            displayOrder: i
          });
        }
        
        questionsResult = await auditService?.getAuditQuestions(audit?.id);
      }
      
      setQuestions(questionsResult?.data || []);
      
      // Load existing responses
      const responsesResult = await auditService?.getAuditResponses(audit?.id);
      if (responsesResult?.data) {
        const responsesMap = {};
        responsesResult?.data?.forEach(response => {
          responsesMap[response?.question_id] = {
            pass: response?.pass,
            comment: response?.comment || ''
          };
        });
        setResponses(responsesMap);
      }
    } catch (err) {
      console.error('[AuditExecutionModal] Error loading audit data:', { code: err?.code, message: err?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId, field, value) => {
    // Clear any stale save error when the user starts editing again so a
    // banner from a previous failed save doesn't sit there forever.
    if (saveError) setSaveError('');
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev?.[questionId],
        [field]: value
      }
    }));
  };

  const calculateScore = () => {
    const answeredQuestions = Object.keys(responses)?.filter(qId => responses?.[qId]?.pass !== undefined);
    const passedQuestions = answeredQuestions?.filter(qId => responses?.[qId]?.pass === true);
    
    if (answeredQuestions?.length === 0) return 0;
    return Math.round((passedQuestions?.length / answeredQuestions?.length) * 100);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveError('');

      // Known partial-failure behaviour: responses are inserted one-by-one
      // in this loop, then updateAudit runs at the end. If a response insert
      // fails mid-loop, earlier responses are already persisted but the
      // audit is not marked read-only. The user retries, which will hit a
      // 23505 unique-constraint violation on the already-inserted responses
      // (or replace them, depending on schema). A proper fix needs an upsert
      // or a transactional RPC — tracked separately, out of scope here.
      for (const questionId of Object.keys(responses)) {
        if (responses?.[questionId]?.pass !== undefined) {
          const respRes = await auditService?.addAuditResponse({
            auditId: audit?.id,
            questionId: questionId,
            pass: responses?.[questionId]?.pass,
            comment: responses?.[questionId]?.comment || null
          });
          if (respRes?.error) throw respRes?.error;
        }
      }

      // Update audit status
      const score = calculateScore();
      // Guard against [].every() === true silently flipping a zero-question
      // audit to 'completed' on save. See wqugEUH0.
      const allAnswered = questions?.length > 0 && questions?.every(q => responses?.[q?.id]?.pass !== undefined);

      const updRes = await auditService?.updateAudit(audit?.id, {
        status: allAnswered ? 'completed' : 'in_progress',
        overall_result: `${score}% Pass Rate`,
        is_read_only: allAnswered
      });
      if (updRes?.error) throw updRes?.error;

      onClose();
    } catch (err) {
      // Surface the real error so the user can react. Modal stays open;
      // user must retry or cancel explicitly.
      console.error('[AuditExecutionModal] Error saving audit:', { code: err?.code, message: err?.message });
      let msg;
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        msg = `Permission denied saving audit: ${err?.message ?? 'unknown error'}`;
      } else if (err?.code === '23502') {
        msg = `Missing required field while saving audit: ${err?.message ?? 'unknown error'}`;
      } else if (err?.code === '23505') {
        msg = `This audit response already exists: ${err?.message ?? 'unknown error'}`;
      } else {
        msg = `Failed to save audit: ${err?.message ?? 'unknown error'}`;
      }
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = audit?.is_read_only || audit?.status === 'completed';
  const score = calculateScore();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="ClipboardCheck" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{audit?.audit_name}</h2>
              <p className="text-sm text-muted-foreground">{audit?.sites?.site_name}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('overallScore')}</p>
                    <p className="text-2xl font-bold text-foreground">{score}%</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground mb-1">{t('progress')}</p>
                    <p className="text-lg font-semibold text-foreground">
                      {Object.keys(responses)?.filter(qId => responses?.[qId]?.pass !== undefined)?.length} / {questions?.length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {questions?.map((question, index) => (
                  <div key={question?.id} className="bg-muted/50 border border-border rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-3">
                      <span className="flex items-center justify-center w-8 h-8 bg-accent/10 rounded-full text-sm font-semibold text-accent">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-foreground mb-1">{question?.question_text}</p>
                        {question?.category && (
                          <p className="text-xs text-muted-foreground">{question?.category}</p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-3 ml-11">
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => !isReadOnly && handleResponseChange(question?.id, 'pass', true)}
                          disabled={isReadOnly}
                          className={`flex-1 px-4 py-2 rounded-md font-medium transition-smooth ${
                            responses?.[question?.id]?.pass === true
                              ? 'bg-success text-success-foreground'
                              : 'bg-background border border-border text-foreground hover:bg-muted'
                          } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <Icon name="CheckCircle" size={16} className="inline mr-2" />
                          {t('pass')}
                        </button>
                        <button
                          type="button"
                          onClick={() => !isReadOnly && handleResponseChange(question?.id, 'pass', false)}
                          disabled={isReadOnly}
                          className={`flex-1 px-4 py-2 rounded-md font-medium transition-smooth ${
                            responses?.[question?.id]?.pass === false
                              ? 'bg-error text-error-foreground'
                              : 'bg-background border border-border text-foreground hover:bg-muted'
                          } ${isReadOnly ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <Icon name="XCircle" size={16} className="inline mr-2" />
                          {t('fail')}
                        </button>
                      </div>

                      {responses?.[question?.id]?.pass !== undefined && (
                        <textarea
                          value={responses?.[question?.id]?.comment || ''}
                          onChange={(e) => !isReadOnly && handleResponseChange(question?.id, 'comment', e?.target?.value)}
                          placeholder={t('addCommentsOrCorrectiveActions')}
                          disabled={isReadOnly}
                          rows={2}
                          className="w-full px-3 py-2 bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background transition-smooth resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {saveError && (
              <div className="flex items-start gap-2 mx-4 md:mx-6 mb-3 bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2">
                <Icon name="AlertCircle" size={14} className="text-destructive shrink-0 mt-0.5" />
                <p className="text-xs text-destructive break-words">{saveError}</p>
              </div>
            )}
            <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
              >
                {isReadOnly ? t('close') : t('cancel')}
              </Button>
              {!isReadOnly && userProfile?.role === 'admin' && (
                <Button
                  type="button"
                  onClick={handleSave}
                  loading={saving}
                  iconName="Save"
                  iconPosition="left"
                >
                  {t('saveProgress')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuditExecutionModal;